import type {
  SnoozeOption,
  TaskCommand,
  TaskCommandType,
  TaskDetailDto,
  TaskListItemDto,
  TaskRequestDto,
} from '@ovo/shared'
import { useQueryClient } from '@tanstack/react-query'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { qk, useTaskAction } from '../api/hooks.ts'
import { formatDue } from '../lib/dates.ts'
import { ACTION_LABELS, INPUT_ACTIONS, listActions, statusLabel } from '../lib/status.ts'
import type { ButtonTone } from '../telegram/buttons.tsx'
import { Chip } from './Chip.tsx'
import type { DeadlineValue } from './DeadlineChips.tsx'
import { DeadlineChips } from './DeadlineChips.tsx'
import { Field, Textarea } from './Input.tsx'
import { IconChevronRight } from './icons.tsx'
import { Cell } from './List.tsx'
import { PersonPicker } from './PersonPicker.tsx'
import { Sheet } from './Sheet.tsx'

// Шторка действий по статусу (StatusActionSheet) и мастера ввода для действий с причиной/сроком/человеком.

export interface ActionTask extends TaskListItemDto {
  requiresResult?: boolean
  requests?: TaskRequestDto[]
}

interface Flow {
  task: ActionTask
  type: TaskCommandType
  requestId?: number
  /** Для submit: результат обязателен. */
  resultRequired?: boolean
  seq: number
}

const SIMPLE: Partial<Record<TaskCommandType, TaskCommand>> = {
  accept: { type: 'accept' },
  start: { type: 'start' },
  verify: { type: 'verify' },
  unblock: { type: 'unblock' },
  on_track: { type: 'on_track' },
  reopen: { type: 'reopen' },
}

interface TextFlowConfig {
  title: string
  placeholder: string
  button: string
  required: boolean
  quick?: string[]
  tone?: ButtonTone
  hint?: string
}

const TEXT_FLOWS: Partial<Record<TaskCommandType, TextFlowConfig>> = {
  decline: {
    title: 'Почему не получится?',
    placeholder: 'Например: на этой неделе занят запуском сайта',
    button: 'Отклонить задачу',
    required: true,
    quick: ['Нет времени на этой неделе', 'Это не моя зона', 'Нужно больше данных'],
    tone: 'danger',
    hint: 'Постановщик увидит причину и сможет передать задачу или договориться о сроке.',
  },
  block: {
    title: 'Что мешает?',
    placeholder: 'Опишите, что нужно, чтобы продолжить',
    button: 'Отметить блокер',
    required: true,
    quick: ['Жду ответа', 'Нет доступа', 'Нужны данные'],
    hint: 'Постановщик получит уведомление — напоминания по сроку пока остановятся.',
  },
  return: {
    title: 'Что доработать?',
    placeholder: 'Напишите, что поправить, — так быстрее',
    button: 'Вернуть на доработку',
    required: true,
  },
  ask: {
    title: 'Ваш вопрос',
    placeholder: 'Что нужно уточнить?',
    button: 'Задать вопрос',
    required: true,
    hint: 'Вопрос уйдёт постановщику в Telegram.',
  },
  answer: { title: 'Ответ на вопрос', placeholder: 'Ваш ответ', button: 'Ответить', required: true },
  cancel: {
    title: 'Отменить задачу?',
    placeholder: 'Причина (необязательно)',
    button: 'Отменить задачу',
    required: false,
    tone: 'danger',
    hint: 'Исполнитель получит уведомление. Задачу можно будет вернуть в работу.',
  },
  decide_due: {
    title: 'Оставить прежний срок?',
    placeholder: 'Комментарий (необязательно)',
    button: 'Оставить прежний срок',
    required: false,
  },
}

const SNOOZE: Array<{ option: SnoozeOption; label: string }> = [
  { option: '1h', label: 'Через час' },
  { option: 'after_lunch', label: 'После обеда' },
  { option: 'tomorrow', label: 'Завтра утром' },
]

function ctrlEnter(onSubmit: () => void) {
  return (e: ReactKeyboardEvent): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      onSubmit()
    }
  }
}

function TextFlowSheet({
  flow,
  config,
  pending,
  onClose,
  onSubmit,
  context,
}: {
  flow: Flow
  config: TextFlowConfig
  pending: boolean
  onClose: () => void
  onSubmit: (text: string) => void
  context?: ReactNode
}) {
  const [text, setText] = useState('')
  const required = flow.type === 'submit' ? Boolean(flow.resultRequired) : config.required
  const ok = !required || text.trim().length > 0
  const submit = (): void => {
    if (ok && !pending) onSubmit(text.trim())
  }
  const onKeyDown = ctrlEnter(submit)
  return (
    <Sheet
      open
      onClose={onClose}
      title={config.title}
      subtitle={flow.task.title}
      primary={{ text: config.button, onClick: submit, disabled: !ok, loading: pending, tone: config.tone }}
    >
      {context}
      {config.quick && (
        <div className="chips chips--wrap">
          {config.quick.map((q) => (
            <Chip key={q} selected={text === q} onClick={() => setText(q)}>
              {q}
            </Chip>
          ))}
        </div>
      )}
      <Field hint={config.hint}>
        {(id) => (
          <Textarea
            id={id}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={config.placeholder}
            aria-label={config.title}
            maxLength={required ? 1000 : 4000}
            rows={3}
            data-autofocus
          />
        )}
      </Field>
    </Sheet>
  )
}

function DueFlowSheet({
  flow,
  pending,
  onClose,
  onSubmit,
}: {
  flow: Flow
  pending: boolean
  onClose: () => void
  onSubmit: (cmd: TaskCommand) => void
}) {
  const isRequest = flow.type === 'request_due'
  const [due, setDue] = useState<DeadlineValue>({ dueAt: flow.task.dueAt, dueKind: flow.task.dueKind })
  const [reason, setReason] = useState('')
  const changed = due.dueAt !== flow.task.dueAt
  const ok = isRequest ? Boolean(due.dueAt) && changed : changed || due.dueKind !== flow.task.dueKind
  const submit = (): void => {
    if (!ok || pending) return
    if (isRequest && due.dueAt) {
      onSubmit({
        type: 'request_due',
        proposedDueAt: due.dueAt,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      })
    } else {
      onSubmit({ type: 'set_due', dueAt: due.dueAt, dueKind: due.dueKind })
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={isRequest ? 'Предложить новый срок' : 'Срок задачи'}
      subtitle={flow.task.title}
      primary={{
        text: isRequest ? 'Предложить срок' : 'Сохранить срок',
        onClick: submit,
        disabled: !ok,
        loading: pending,
      }}
    >
      {flow.task.dueAt && (
        <div className="hint hint--md">Сейчас: {formatDue(flow.task.dueAt, flow.task.dueKind)}</div>
      )}
      <DeadlineChips value={due} onChange={setDue} allowNone={!isRequest} />
      {isRequest && (
        <Field
          label="Почему нужно больше времени?"
          hint="Открытый перенос лучше молчаливого срыва — постановщик решит одной кнопкой."
        >
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Необязательно"
              rows={2}
              maxLength={1000}
            />
          )}
        </Field>
      )}
    </Sheet>
  )
}

function FlowSheet({
  flow,
  meId,
  pending,
  onClose,
  onSubmit,
}: {
  flow: Flow
  meId: number
  pending: boolean
  onClose: () => void
  onSubmit: (cmd: TaskCommand) => void
}) {
  const { type, task } = flow

  if (type === 'request_due' || type === 'set_due') {
    return <DueFlowSheet flow={flow} pending={pending} onClose={onClose} onSubmit={onSubmit} />
  }

  if (type === 'reassign') {
    return (
      <PersonPicker
        open
        onClose={onClose}
        title="Передать задачу"
        meId={meId}
        excludeIds={[task.assignee.id]}
        onPick={(u) => onSubmit({ type: 'reassign', assigneeId: u.id })}
      />
    )
  }

  if (type === 'snooze') {
    return (
      <Sheet open onClose={onClose} title="Напомнить позже" subtitle={task.title}>
        <div className="section__body">
          {SNOOZE.map((s) => (
            <Cell
              key={s.option}
              title={s.label}
              onClick={() => onSubmit({ type: 'snooze', option: s.option })}
            />
          ))}
        </div>
        <div className="hint">Срок от этого не сдвинется — только напоминание.</div>
      </Sheet>
    )
  }

  if (type === 'submit') {
    const config: TextFlowConfig = {
      title: 'Что сделано?',
      placeholder: flow.resultRequired
        ? 'Опишите результат или дайте ссылку'
        : 'Коротко опишите результат (необязательно)',
      button: 'Готово',
      required: Boolean(flow.resultRequired),
      tone: 'done',
      hint: flow.resultRequired ? 'Постановщик попросил приложить результат.' : undefined,
    }
    return (
      <TextFlowSheet
        flow={flow}
        config={config}
        pending={pending}
        onClose={onClose}
        onSubmit={(text) => onSubmit({ type: 'submit', ...(text ? { resultText: text } : {}) })}
      />
    )
  }

  const config = TEXT_FLOWS[type]
  if (!config) return null
  const request = flow.requestId ? task.requests?.find((r) => r.id === flow.requestId) : undefined

  const context =
    type === 'answer' && request?.text ? (
      <div className="banner">
        <div className="banner__body">
          <div className="banner__title">Вопрос от {request.requestedBy.displayName}</div>
          <div className="prewrap">{request.text}</div>
        </div>
      </div>
    ) : type === 'decide_due' && request?.proposedDueAt ? (
      <div className="hint hint--md">
        Предложенный срок: {formatDue(request.proposedDueAt)}. Исполнитель получит ваш ответ.
      </div>
    ) : undefined

  return (
    <TextFlowSheet
      flow={flow}
      config={config}
      pending={pending}
      context={context}
      onClose={onClose}
      onSubmit={(text) => {
        switch (type) {
          case 'decline':
            onSubmit({ type: 'decline', reason: text })
            break
          case 'block':
            onSubmit({ type: 'block', reason: text })
            break
          case 'return':
            onSubmit({ type: 'return', comment: text })
            break
          case 'ask':
            onSubmit({ type: 'ask', text })
            break
          case 'answer':
            if (flow.requestId) onSubmit({ type: 'answer', requestId: flow.requestId, text })
            break
          case 'cancel':
            onSubmit({ type: 'cancel', ...(text ? { reason: text } : {}) })
            break
          case 'decide_due':
            if (flow.requestId) {
              onSubmit({
                type: 'decide_due',
                requestId: flow.requestId,
                approve: false,
                ...(text ? { note: text } : {}),
              })
            }
            break
          default:
            break
        }
      }}
    />
  )
}

/** Шторка «что сделать с задачей»: только доступные переходы + «Открыть задачу». */
export function StatusActionSheet({
  task,
  actions,
  onClose,
  onPick,
  onOpen,
}: {
  task: ActionTask | null
  actions: TaskCommandType[]
  onClose: () => void
  onPick: (task: ActionTask, action: TaskCommandType) => void
  onOpen?: (task: ActionTask) => void
}) {
  return (
    <Sheet
      open={task !== null}
      onClose={onClose}
      title={task?.title}
      subtitle={task ? `Сейчас: ${statusLabel(task.status)}` : undefined}
    >
      {task && (
        <div className="section__body">
          {actions.map((a) => (
            <Cell
              key={a}
              title={ACTION_LABELS[a]}
              danger={a === 'decline' || a === 'cancel'}
              onClick={() => onPick(task, a)}
            />
          ))}
          {onOpen && (
            <Cell
              title="Открыть задачу"
              after={<IconChevronRight size={18} className="chevron" />}
              onClick={() => onOpen(task)}
            />
          )}
        </div>
      )}
    </Sheet>
  )
}

/**
 * Действия над задачей из любого экрана: run(task, 'decline') откроет нужную шторку,
 * простые действия отправит сразу. element нужно отрисовать на экране.
 */
export function useTaskActions(meId: number) {
  const qc = useQueryClient()
  const action = useTaskAction()
  const navigate = useNavigate()
  const [menu, setMenu] = useState<{
    task: ActionTask
    actions: TaskCommandType[]
    showOpen: boolean
  } | null>(null)
  const [flow, setFlow] = useState<Flow | null>(null)
  const [seq, setSeq] = useState(0)

  const send = (task: ActionTask, command: TaskCommand): void => {
    action.mutate(
      { taskId: task.id, command, task },
      {
        onSuccess: () => setFlow(null),
      },
    )
  }

  const startFlow = (f: Omit<Flow, 'seq'>): void => {
    setSeq((s) => s + 1)
    setFlow({ ...f, seq: seq + 1 })
  }

  const run = (
    task: ActionTask,
    type: TaskCommandType,
    extra?: { requestId?: number; approve?: boolean },
  ): void => {
    setMenu(null)
    if (type === 'edit') {
      navigate(`/tasks/${task.id}/edit`)
      return
    }
    if (type === 'decide_due' && extra?.approve && extra.requestId) {
      send(task, { type: 'decide_due', requestId: extra.requestId, approve: true })
      return
    }
    if (type === 'submit') {
      const rr = task.requiresResult ?? qc.getQueryData<TaskDetailDto>(qk.task(task.id))?.requiresResult
      if (rr === false) send(task, { type: 'submit' })
      else startFlow({ task, type, resultRequired: rr === true })
      return
    }
    const simple = SIMPLE[type]
    if (simple) {
      send(task, simple)
      return
    }
    if (INPUT_ACTIONS.has(type)) startFlow({ task, type, requestId: extra?.requestId })
  }

  const openMenu = (
    task: ActionTask,
    actions: TaskCommandType[] = listActions(task, meId),
    showOpen = true,
  ): void => {
    setMenu({ task, actions, showOpen })
  }

  const element = (
    <>
      <StatusActionSheet
        task={menu?.task ?? null}
        actions={menu?.actions ?? []}
        onClose={() => setMenu(null)}
        onPick={(task, a) => run(task, a)}
        onOpen={
          menu?.showOpen === false
            ? undefined
            : (task) => {
                setMenu(null)
                navigate(`/tasks/${task.id}`)
              }
        }
      />
      {flow && (
        <FlowSheet
          key={flow.seq}
          flow={flow}
          meId={meId}
          pending={action.isPending}
          onClose={() => setFlow(null)}
          onSubmit={(cmd) => send(flow.task, cmd)}
        />
      )}
    </>
  )

  return {
    run,
    openMenu,
    element,
    isPending: action.isPending,
    pendingTaskId: action.isPending ? (action.variables?.taskId ?? null) : null,
  }
}
